import { useState } from 'react';
import {
  Alert,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { authApi } from '../../../services/api/authApi';
import PasswordField from '../PasswordField';
import {
  authIconSx,
  authInputSx,
  authLabelSx,
  authPrimaryBtnSx,
  authLinkBtnSx,
} from '../authFormTheme';

function ForgotPasswordScreen({ onBack, onResetSuccess }) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const fullPhone = phone.startsWith('+') ? phone : `+91${phone.replace(/\D/g, '')}`;

  const resetPassword = async () => {
    if (!phone.trim()) {
      setError('Enter your registered phone number');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');
    try {
      const result = await authApi.resetPassword({
        phone: fullPhone,
        password,
      });
      setInfo(result.message || 'Password reset successful');
      setTimeout(() => onResetSuccess?.(), 1500);
    } catch (err) {
      setError(err.message || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Button
        startIcon={<ArrowBackIosNewRoundedIcon sx={authIconSx} />}
        onClick={onBack}
        sx={{ ...authLinkBtnSx, alignSelf: 'flex-start', py: 0.5, px: 1 }}
      >
        Back to login
      </Button>

      <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff' }}>
        Reset password
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem', fontWeight: 600 }}>
        Enter your registered phone number and set a new password.
      </Typography>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {info ? <Alert severity="success">{info}</Alert> : null}

      <Typography sx={authLabelSx}>
        <PhoneAndroidRoundedIcon sx={authIconSx} fontSize="small" />
        Phone number
      </Typography>
      <Stack direction="row" spacing={1.2}>
        <TextField size="small" defaultValue="+91" sx={{ ...authInputSx, width: 95 }} disabled />
        <TextField
          size="small"
          fullWidth
          placeholder="Phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          sx={authInputSx}
        />
      </Stack>

      <Typography sx={authLabelSx}>
        <LockRoundedIcon sx={authIconSx} fontSize="small" />
        New password
      </Typography>
      <PasswordField
        placeholder="New password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        showPassword={showPassword}
        onTogglePassword={() => setShowPassword((v) => !v)}
      />

      <Typography sx={authLabelSx}>
        <LockRoundedIcon sx={authIconSx} fontSize="small" />
        Verify password
      </Typography>
      <PasswordField
        placeholder="Confirm new password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        showPassword={showConfirm}
        onTogglePassword={() => setShowConfirm((v) => !v)}
      />

      <Button
        variant="contained"
        size="large"
        disabled={loading}
        onClick={resetPassword}
        sx={authPrimaryBtnSx}
      >
        {loading ? 'Updating…' : 'Change password'}
      </Button>
    </Stack>
  );
}

export default ForgotPasswordScreen;
